import { blackBookAtPosition, blackBookFor, isBlackOpening } from "../content/blackCourses.ts";
import { isOpeningId, type OpeningId } from "../lessons/ids.ts";
import { loadedCourseById } from "../content/lookup.ts";
import {
  ALAPIN_MAINLINE,
  ENGLISH_MAINLINE,
  FOUR_KNIGHTS_MAINLINE,
  ITALIAN_MAINLINE,
  KINGS_GAMBIT_MAINLINE,
  PIRC_MAINLINE,
  FRENCH_ADVANCE_MAINLINE,
  SCANDINAVIAN_MAINLINE,
  CARO_ADVANCE_MAINLINE,
  QUIET_TWO_KNIGHTS_MAINLINE,
  LONDON_MAINLINE,
  OPEN_SICILIAN_MAINLINE,
  QUEENS_GAMBIT_MAINLINE,
  RETI_MAINLINE,
  RUY_LOPEZ_MAINLINE,
  SCOTCH_MAINLINE,
  VIENNA_MAINLINE,
} from "../content/mainlines.ts";
import { Chess } from "@vendor/chess.js";
import { positionKey } from "../chess/positionKey.ts";
import {
  onWhiteCoachingRegistered,
  registeredWhiteCoaching,
  whiteCoachingPack,
} from "../content/whiteCoachingPacks.ts";

/** The strategic layer of a move note: why the move fits the opening's plan. */
export type StrategicLesson = {
  strategy: string;
  tradeoff: string;
  blackPlan: string;
  takeaway: string;
};

export type BookCoachingNote = {
  moveNumber: number;
  san: string;
  goal: string;
  piece: string;
  candidates: string;
  why: string;
  next: string;
  watch: string;
  branches?: string;
  options?: BookAlternative[];
  depth?: StrategicLesson;
};

export type BookAlternative = {
  san: string;
  name: string;
  why: string;
  watch?: string;
  /** Illustrative continuation, beginning with the student's move. */
  line?: string[];
  /** Deeper notes keyed by the zero-based half-move index in `line`. */
  continuation?: Record<number, { why: string; next: string; watch: string }>;
  moveNumber?: number;
};

export type BoundaryGuide = {
  goal: string;
  piece: string;
  next: string;
  watch: string;
  depth?: StrategicLesson;
};

export type CoachingLayer = {
  kind: "book" | "boundary";
  title: string;
  plan: string;
  watch: string;
  branches?: string;
  depth?: StrategicLesson;
};

type OpeningBook = readonly BookCoachingNote[];

export const MAINLINES: Partial<Record<OpeningId, readonly string[]>> = {
  italian: ITALIAN_MAINLINE,
  queensGambit: QUEENS_GAMBIT_MAINLINE,
  ruyLopez: RUY_LOPEZ_MAINLINE,
  english: ENGLISH_MAINLINE,
  london: LONDON_MAINLINE,
  alapin: ALAPIN_MAINLINE,
  vienna: VIENNA_MAINLINE,
  scotch: SCOTCH_MAINLINE,
  fourKnights: FOUR_KNIGHTS_MAINLINE,
  reti: RETI_MAINLINE,
  openSicilian: OPEN_SICILIAN_MAINLINE,
  kingsGambit: KINGS_GAMBIT_MAINLINE,
  pircModern: PIRC_MAINLINE,
  frenchAdvance: FRENCH_ADVANCE_MAINLINE,
  scandinavian: SCANDINAVIAN_MAINLINE,
  caroAdvance: CARO_ADVANCE_MAINLINE,
  quietTwoKnights: QUIET_TWO_KNIGHTS_MAINLINE,
};

/** A course's notes, from its pack once the course has loaded. */
function bookFor(openingId: string | null | undefined): OpeningBook | undefined {
  return whiteCoachingPack(openingId)?.book;
}

let positionNotes: Map<string, BookCoachingNote> | null = null;
// A course's coaching may arrive later with its body; rebuild the index then.
onWhiteCoachingRegistered(() => {
  positionNotes = null;
});

/** Match the authored position, including side to move and castling rights.
 * Move clocks are deliberately ignored so a genuine transposition still works.
 */
export function bookCoachingAtPosition(
  openingId: string | null | undefined,
  fen: string,
  san?: string | null
): BookCoachingNote | null {
  if (isBlackOpening(openingId)) return blackBookAtPosition(openingId, fen, san);
  if (!positionNotes) {
    positionNotes = new Map();
    for (const [id, line] of Object.entries(MAINLINES)) {
      const chess = new Chess();
      for (let ply = 0; ply < 30; ply += 1) {
        if (ply % 2 === 0) {
          const note = bookCoachingFor(id, ply / 2 + 1);
          if (note) positionNotes.set(`${id}:${positionKey(chess.fen())}`, note);
        }
        chess.move(line[ply]);
      }
    }
  }
  const note =
    positionNotes.get(`${openingId}:${positionKey(fen)}`) || graphNoteAtPosition(openingId, fen);
  if (!note || !san) return note;
  return normalizeSan(note.san) === normalizeSan(san) ? note : null;
}

/**
 * A lesson authored on a taught move outside the main line: a side line's
 * White move whose PGN note carries piece, candidates, next and watch fields.
 * Such a move is coached exactly like a main-line move.
 */
function graphNoteAtPosition(
  openingId: string | null | undefined,
  fen: string
): BookCoachingNote | null {
  if (!openingId || !isOpeningId(openingId)) return null;
  const course = loadedCourseById(openingId);
  const node = course?.nodes[positionKey(fen)];
  if (!node) return null;
  const move = node.moves.find(
    (m) => (m.role === "repertoire" || m.role === "transposition") && m.next && m.watch
  );
  if (!move || !move.next || !move.watch) return null;
  return {
    moveNumber: Math.floor(node.ply / 2) + 1,
    san: move.san,
    goal: node.objectiveAuthored ? node.objective : `Play ${move.san}. ${move.explanation}`,
    piece: move.piece || `Look at the piece that ${move.san} moves and the squares it reaches.`,
    candidates: move.candidates || `The course plays ${move.san} here.`,
    why: move.explanation,
    next: move.next,
    watch: move.watch,
  };
}

function normalizeSan(san: string): string {
  return san.replace(/[+#]/g, "");
}

export function bookCoachingFor(
  openingId: string | null | undefined,
  moveNumber: number,
  san?: string | null
): BookCoachingNote | null {
  if (isBlackOpening(openingId)) {
    const note = blackBookFor(openingId, moveNumber);
    return note && (!san || normalizeSan(note.san) === normalizeSan(san)) ? note : null;
  }
  const book = bookFor(openingId);
  if (!book) return null;
  const note = book.find((item) => item.moveNumber === moveNumber) || null;
  if (!note || (san && normalizeSan(note.san) !== normalizeSan(san))) return null;
  return { ...note };
}

export function boundaryGuideFor(
  openingId: string | null | undefined,
  moveNumber: number
): BoundaryGuide | null {
  const guides = whiteCoachingPack(openingId)?.boundary;
  if (!guides) return null;
  const stage = moveNumber <= 4 ? 0 : moveNumber <= 7 ? 1 : 2;
  return guides[stage];
}

export function validateBookCoaching(): string[] {
  const issues: string[] = [];
  const books: [string, OpeningBook][] = registeredWhiteCoaching().map(
    ([id, pack]): [string, OpeningBook] => [id, pack.book]
  );
  for (const [openingId, notes] of books) {
    if (notes.length < 15) issues.push(`${openingId}: fewer than fifteen coached moves`);
    for (let moveNumber = 1; moveNumber <= 15; moveNumber += 1) {
      const note = notes.find((item) => item.moveNumber === moveNumber);
      if (!note) {
        issues.push(`${openingId}: missing move ${moveNumber}`);
        continue;
      }
      if (moveNumber >= 3) {
        const depth = note.depth;
        if (!depth || Object.values(depth).some((text) => !text.trim())) {
          issues.push(`${openingId}: missing strategic depth for move ${moveNumber}`);
        }
      }
      if (
        !note.san ||
        !note.goal ||
        !note.piece ||
        !note.candidates ||
        !note.why ||
        !note.next ||
        !note.watch
      ) {
        issues.push(`${openingId}: incomplete move ${moveNumber}`);
      }
    }
  }
  for (const [id, line] of Object.entries(MAINLINES)) {
    // A pack course's notes exist only once its body has loaded.
    if (!bookFor(id)) continue;
    const chess = new Chess();
    for (const [ply, san] of line.entries()) {
      if (ply % 2 === 0) {
        const note = bookCoachingFor(id, ply / 2 + 1);
        if (note?.san !== san) issues.push(`${id}: coaching SAN differs at move ${ply / 2 + 1}`);
        const seen = new Set<string>();
        for (const option of note?.options || []) {
          if (seen.has(option.san) || option.san === san)
            issues.push(`${id}: duplicate alternative ${option.san}`);
          seen.add(option.san);
          if (!chess.moves().includes(option.san))
            issues.push(`${id}: illegal alternative ${option.san}`);
          if (option.line) {
            const branch = new Chess(chess.fen());
            if (option.line[0] !== option.san || option.line.length < 4 || !option.watch?.trim()) {
              issues.push(`${id}: incomplete continuation for ${option.san}`);
            }
            for (const reply of option.line) {
              if (!branch.moves().includes(reply)) {
                issues.push(`${id}: illegal continuation ${reply} after ${option.san}`);
                break;
              }
              branch.move(reply);
            }
          }
        }
      }
      chess.move(san);
    }
  }
  return issues;
}
